// THE LONG KINDLING — Room 5: "The Forge" (Act I / into II) — the axe; the slow craft.
// Authored from research/wall/script/v3/rooms_01-05.md §Room 5 and BUILD_PLAN.md, copying the proven
// hearth_hall.ts pattern (field names, fx/fire wiring, the reversible-shortcut toggle pattern).
//
// Painting public/art/forge.png is 384x216; the room is 480x270 (ART→ROOM x1.25). The forge-coal fire
// CUTOUT (art/forge_fire.png, baked by tools/gen_firemask.mjs over the coal-bed under the bellows) is
// twinkled automatically (renderer derives `<bgImage>`→`_fire.png`).
//
// SIGNATURE Chesterton's-Fence puzzle: TEMPER (KEEP, the slow true way) vs WRAP (the quick shortcut that
// feels better and won't last). Modelled as one reversible boolean `axe_wrapped` so it can NEVER soft-
// lock: KEEPing the temper sets axe_wrapped=false AND yields the global `good_axe` (the R14 felling
// key); the WRAP shortcut sets axe_wrapped=true (the wrapped axe visibly fails at the line, R14).
// The good axe is taken via tempering; coming back for a proper temper is always available (reversible).
import { loadRoom, type Room } from "../../core/schema";

export const forge = {
  id: "forge",
  name: "The Forge",
  size: { w: 480, h: 270 },
  background: { color: 0x140e08 },
  bgImage: "art/forge.png",
  // hero starts on the scale-glittered floor, near the door (the axe-rack leans against the wall)
  spawn: { x: 330, y: 244 },
  actorRadius: 8,
  interactRadius: 64,
  // the handle's two states: tempered true (right/default; yields the good axe) vs wrapped (the shortcut).
  state: { axe_wrapped: false },
  walkmesh: [
    [ { x: 40, y: 224 }, { x: 452, y: 224 }, { x: 452, y: 266 }, { x: 36, y: 266 } ],
  ],
  hotspots: [
    // ---- KEEP the temper — the slow true way; yields the good axe (global good_axe), clears the wrap shortcut
    { id: "temper_axe", verb: "use", label: "temper the axe", interactAt: { x: 232, y: 244 },
      line: "Brannoch reads the colour as it heats — straw, then bronze, then a blue like a winter dusk — and quenches at the right shade. You work the bellows, watch the colour, and quench at the blue on his word. Out comes the good axe, true to the hard grain at the line. KEEP: the slow temper is the only thing that holds an edge.",
      effect: { set: { axe_wrapped: false }, setGlobal: { good_axe: true } },
      polygon: [ { x: 206, y: 168 }, { x: 262, y: 168 }, { x: 262, y: 214 }, { x: 206, y: 214 } ] },

    // ---- the leather wrap — the Hush-shortcut; feels grand, fails at the line (reversible: come back to temper)
    { id: "leather_wrap", verb: "use", label: "the leather wrap", interactAt: { x: 296, y: 244 },
      line: "A wrap for the handle — quick, cheap, feels grand in the hand for a day. Wrap it instead of waiting on the slow temper and it swings sweet at once — but the edge is untempered, and at the felling-front it rolls in the hard grain and you come back having wasted the day. 'It'll hold till it doesn't,' Brannoch says. (Reversible: come back for the proper temper.)",
      effect: { set: { axe_wrapped: true } },
      polygon: [ { x: 274, y: 192 }, { x: 318, y: 192 }, { x: 318, y: 216 }, { x: 274, y: 216 } ] },

    // ---- the axe-rack / dulled edges — the failing watch made physical in steel (look-at only)
    { id: "axe_rack", verb: "examine", label: "the axe-rack", interactAt: { x: 168, y: 244 },
      line: "Good steel, all of it, and all of it gone soft at the lip by noon. The wood at the line's turned hard these last years — the grain like iron, the men coming home grey-faced. Brannoch can't temper them fast enough.",
      polygon: [ { x: 144, y: 150 }, { x: 192, y: 150 }, { x: 192, y: 212 }, { x: 144, y: 212 } ] },

    // ---- the slack-tub & bellows — texture; sells the heat and the labour (look-at only)
    { id: "slack_tub", verb: "examine", label: "the slack-tub & bellows", interactAt: { x: 408, y: 244 },
      line: "The water hisses when the steel goes in. That hiss and the bellows are the only voices in here — Brannoch doesn't waste words.",
      polygon: [ { x: 388, y: 178 }, { x: 440, y: 178 }, { x: 440, y: 216 }, { x: 388, y: 216 } ] },

    // ---- the temper-colours — seeds patience-is-the-skill (look-at only)
    { id: "temper_colours", verb: "examine", label: "the temper-colours", interactAt: { x: 132, y: 244 },
      line: "Brannoch reads the colour as it heats — straw, then bronze, then a blue like a winter dusk — and quenches at the right shade. Too soon it's brittle, too late it's soft. All in the eye. You can't hurry it.",
      polygon: [ { x: 104, y: 130 }, { x: 156, y: 130 }, { x: 156, y: 196 }, { x: 104, y: 196 } ] },
  ],
  // living-scene fx: the forge fire breathes orange in the arched hearth (glow+flames+embers over the
  // cutout). The corners are cold and blue — warm paint pooled tight around the fire.
  // forge fire at ART(310,95) -> ROOM(388,119).
  fx: [
    { id: "coal_glow", kind: "glow", layer: "back", x: 356, y: 110, w: 64, h: 70, color: 0xff8a30, intensity: 1.2, seed: 1 },
    { id: "coal_flames", kind: "flames", layer: "back", x: 368, y: 116, w: 44, h: 52, intensity: 1.15, seed: 2 },
    { id: "coal_embers", kind: "embers", layer: "front", x: 374, y: 108, w: 38, h: 56, count: 16, intensity: 1.15, seed: 3 },
  ],
  npcs: [
    // Brannoch the smith — few words, dry, proud of slow work; knows in his hands the quick fix fails.
    { id: "brannoch", x: 244, y: 240, radius: 9, color: 0x5c5048, label: "Brannoch the smith",
      dialog: [
        { text: "Temper takes the time it takes. Heat, watch the colour, quench true. Hurry it and you've a pretty axe that won't bite.", mood: "neutral" },
        { text: "They want the wrap. Feels good today. I'll wrap it if they make me — but I'll tell 'em first it'll hold till it doesn't, and then I'll bury 'em with the rest.", mood: "wary" },
        { text: "Wood's gone hard at the line. Wasn't always. Something's changed out there past the last field, and nobody'll say what. The watchers want more wood off us every year, seems to me, to keep that fire of theirs.", mood: "wary" },
      ] },
  ],
  // Exit to the Green & Well (R7) — wired when R7 lands; commented so the world boots clean.
  exits: [
    // the door -> the Green & Well (R7)
    { id: "to_green", target: "green_well", spawn: { x: 240, y: 244 }, transition: "fade",
      polygon: [ { x: 306, y: 226 }, { x: 358, y: 226 }, { x: 358, y: 250 }, { x: 306, y: 250 } ] },
  ],
};

export const room: Room = loadRoom(forge);
